import type { CaptureOptions as SnapdomOptions } from "@zumer/snapdom";
import { snapdom } from "@zumer/snapdom";
import { sanitizeFilename } from "./filename";

/**
 * キャプチャ時にターゲット要素へ適用する前処理の型。
 * cleanup 関数を返すことで、呼び出し側は後処理を委譲できる。
 */
export type ElementPreparation = (element: HTMLElement) => void | VoidFunction;

const DEFAULT_BACKGROUND = "transparent";

const RECHARTS_WRAPPER_SELECTOR = ".recharts-wrapper";
const MAX_RECHARTS_WAIT_ATTEMPTS = 12;
const DEFAULT_QUALITY = 0.98;
const DEFAULT_SCALE = 1;
// DOMの寸法は維持しつつ滑らかさだけを向上させたいので、DPRだけ標準より少し高く設定
const DEFAULT_DPR = 1.5;

/**
 * 指定したフレーム数だけ描画フレームを待機する。
 * @param frames 待機したいアニメーションフレームの数。
 * @returns 指定フレームの経過後に解決される Promise。
 */
function waitForFrames(frames: number): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const step = (remaining: number) => {
      if (remaining <= 0) {
        resolve();
        return;
      }
      window.requestAnimationFrame(() => step(remaining - 1));
    };
    step(frames);
  });
}

/**
 * Recharts のラッパー要素がレンダリングされるまで待機する。
 * @param element 監視対象となるグラフのルート要素。
 * @returns ラッパー出現後に解決される Promise。
 */
async function waitForRechartsWrapper(element: HTMLElement): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  const isReady = () =>
    element.querySelector(RECHARTS_WRAPPER_SELECTOR) !== null;

  if (isReady()) {
    return;
  }

  await new Promise<void>((resolve) => {
    let attempts = 0;

    const check = () => {
      if (isReady() || attempts >= MAX_RECHARTS_WAIT_ATTEMPTS) {
        resolve();
        return;
      }

      attempts += 1;
      window.requestAnimationFrame(check);
    };

    check();
  });
}

type CaptureOverrides = {
  backgroundColor?: string;
  quality?: number;
  scale?: number;
  dpr?: number;
};

/**
 * クローン生成時に返すべき情報。
 * @property element - キャプチャ対象となる複製要素。
 * @property cleanup - キャプチャ完了後に呼び出すクリーンアップ関数。
 */
export type CloneForCaptureResult = {
  element: HTMLElement;
  cleanup: VoidFunction;
};

/**
 * キャプチャ専用に要素をオフスクリーンへ複製する関数シグネチャ。
 * @param original - オリジナルのDOM要素。
 * @returns クローンと後処理をまとめた結果。複製不要な場合は null。
 */
export type CloneForCapture = (
  original: HTMLElement,
) => CloneForCaptureResult | null;

export type CaptureOptions = CaptureOverrides & {
  prepare?: ElementPreparation;
  clone?: CloneForCapture;
};

type ClipboardItemConstructor = new (
  items: Record<string, Blob>,
) => ClipboardItem;

/**
 * snapdom の標準設定で要素を PNG Blob として描画する。
 * @param element キャプチャ対象となる DOM 要素。
 * @param options 画質や準備処理、クローン生成ロジックなどのオプション。
 * @returns キャプチャ結果の PNG Blob。
 */
async function captureElementAsPngBlob(
  element: HTMLElement,
  options: CaptureOptions = {},
): Promise<Blob> {
  const { prepare, backgroundColor, quality, scale, dpr, clone } = options;

  const snapdomOptions: SnapdomOptions = {
    fast: false,
    embedFonts: true,
    backgroundColor: backgroundColor ?? DEFAULT_BACKGROUND,
    quality: quality ?? DEFAULT_QUALITY,
    scale: scale ?? DEFAULT_SCALE,
    dpr: dpr ?? DEFAULT_DPR,
    type: "png",
    format: "png",
  };

  let targetElement = element;
  let clonedCleanup: VoidFunction | null = null;

  if (clone) {
    const cloned = clone(element);
    if (cloned) {
      targetElement = cloned.element;
      clonedCleanup = cloned.cleanup;
    }
  }

  await waitForRechartsWrapper(targetElement);

  await waitForFrames(1);

  const cleanup = prepare?.(targetElement) ?? null;

  await waitForFrames(1);

  snapdomOptions.scale = scale ?? DEFAULT_SCALE;

  try {
    const instance = await snapdom(targetElement, snapdomOptions);
    const blob = await instance.toBlob(snapdomOptions);

    if (!blob) {
      throw new Error("Failed to capture element as PNG.");
    }

    return blob;
  } finally {
    cleanup?.();
    clonedCleanup?.();
  }
}

/**
 * キャプチャ結果をクリップボードへ書き込む。
 * @param element キャプチャ対象となる DOM 要素。
 * @param options キャプチャ時に適用するオプション。
 */
export async function copyElementAsPng(
  element: HTMLElement,
  options?: CaptureOptions,
): Promise<void> {
  if (typeof navigator === "undefined" || !navigator.clipboard?.write) {
    throw new Error("Clipboard API is unavailable in this context.");
  }

  const ClipboardItemCtor = (
    window as Window & { ClipboardItem?: ClipboardItemConstructor }
  ).ClipboardItem;
  if (!ClipboardItemCtor) {
    throw new Error("ClipboardItem is not supported by this browser.");
  }

  const blob = await captureElementAsPngBlob(element, options);
  const item = new ClipboardItemCtor({ "image/png": blob });
  await navigator.clipboard.write([item]);
}

export type DownloadOptions = CaptureOptions & {
  filename?: string;
};

/**
 * キャプチャ結果を PNG ファイルとしてダウンロードさせる。
 * @param element キャプチャ対象となる DOM 要素。
 * @param options ファイル名やキャプチャ設定を含むオプション。
 */
export async function downloadElementAsPng(
  element: HTMLElement,
  { filename = "snapshot", ...options }: DownloadOptions = {},
): Promise<void> {
  const blob = await captureElementAsPngBlob(element, options);
  const sanitized = sanitizeFilename(filename);
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = sanitized.endsWith(".png") ? sanitized : `${sanitized}.png`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
}
